import { Fragment, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { FileText, Folder } from "lucide-react";
import { cn } from "@weco/ui";

/**
 * Restricted conversation formatter for the Mochi companion popover.
 *
 * Only `**bold**` and `- `/`* `/`• ` bullets are understood; markdown headings
 * (`#{1,6} `) render as a bold line. Nothing here ever uses
 * dangerouslySetInnerHTML or otherwise injects raw markup — arbitrary text
 * (including literal HTML tags) always renders as plain text content.
 */

const VAULT_REF = /\[\[(file|folder):([^|]+)\|([^\]]+)\]\]/g;

export function Bubble({ role, children }: { role: "user" | "assistant"; children: ReactNode }) {
  return (
    <div className={cn("flex", role === "user" ? "justify-end" : "justify-start")}>
      <div
        className={cn(
          "max-w-[85%] whitespace-pre-wrap break-words rounded-3xl px-3.5 py-2 text-sm leading-relaxed",
          role === "user"
            ? "rounded-br-md bg-[rgba(51,102,255,0.10)] text-slate-800 ring-1 ring-inset ring-[rgba(51,102,255,0.20)]"
            : "rounded-bl-md bg-white/75 text-slate-700 ring-1 ring-inset ring-white",
        )}
      >
        {children}
      </div>
    </div>
  );
}

export function FormattedText({ text }: { text: string }) {
  const blocks: Array<{ kind: "p" | "ul"; lines: string[] }> = [];
  for (const raw of text.split("\n")) {
    // Markdown headings read as a bold line.
    const heading = /^#{1,6}\s+(.*)$/.exec(raw);
    const line = heading ? `**${heading[1]}**` : raw;
    const bullet = /^\s*[-*•]\s+(.*)$/.exec(line);
    const last = blocks.at(-1);
    if (bullet) {
      if (last?.kind === "ul") last.lines.push(bullet[1] ?? "");
      else blocks.push({ kind: "ul", lines: [bullet[1] ?? ""] });
    } else if (last?.kind === "p") {
      last.lines.push(line);
    } else {
      blocks.push({ kind: "p", lines: [line] });
    }
  }
  return (
    <>
      {blocks.map((block, index) =>
        block.kind === "ul" ? (
          <ul key={index} className="my-1 list-disc space-y-0.5 whitespace-normal pl-5">
            {block.lines.map((line, lineIndex) => (
              <li key={lineIndex}>
                <Inline text={line} />
              </li>
            ))}
          </ul>
        ) : (
          <Fragment key={index}>
            <Inline text={block.lines.join("\n").replace(/^\n+|\n+$/g, "")} />
          </Fragment>
        ),
      )}
    </>
  );
}

function VaultReference({ type, id, name }: { type: string; id: string; name: string }) {
  const Icon = type === "file" ? FileText : Folder;
  return (
    <Link
      to={type === "file" ? `/vault?fileId=${encodeURIComponent(id)}` : `/vault/folder/${encodeURIComponent(id)}`}
      className="mx-0.5 inline-flex max-w-full items-center gap-1 rounded-full bg-[rgba(51,102,255,0.10)] px-2 py-0.5 align-baseline text-xs font-semibold text-[rgb(51,102,255)] ring-1 ring-inset ring-[rgba(51,102,255,0.20)] hover:bg-[rgba(51,102,255,0.15)]"
    >
      <Icon className="h-3 w-3 shrink-0" aria-hidden />
      <span className="truncate">{name}</span>
    </Link>
  );
}

export function Inline({ text }: { text: string }) {
  const pieces: ReactNode[] = [];
  let last = 0;
  for (const match of text.matchAll(new RegExp(VAULT_REF.source, "g"))) {
    const at = match.index ?? 0;
    if (at > last) pieces.push(<Bold key={`t${last}`} text={text.slice(last, at)} />);
    pieces.push(<VaultReference key={`r${at}`} type={match[1] ?? "file"} id={match[2] ?? ""} name={match[3] ?? ""} />);
    last = at + match[0].length;
  }
  if (last < text.length) pieces.push(<Bold key={`t${last}`} text={text.slice(last)} />);
  return <>{pieces}</>;
}

export function Bold({ text }: { text: string }) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((part, index) =>
        part.startsWith("**") && part.endsWith("**") && part.length > 4 ? (
          <strong key={index} className="font-semibold text-slate-900">
            {part.slice(2, -2)}
          </strong>
        ) : (
          <Fragment key={index}>{part}</Fragment>
        ),
      )}
    </>
  );
}
